/**
 * @fileoverview 最近浏览市场 Hook
 * @description 使用 localStorage 持久化记录用户最近浏览过的市场 ID，
 *              进入市场详情页时自动记录，市场列表页顶部展示最近 3 条
 * @module hooks/useRecentMarkets
 */

import { useCallback, useEffect, useState } from 'react';
import type { Market } from '../types';

/** localStorage 存储键名 */
const STORAGE_KEY = 'pythia_recent_markets';

/** 最大记录条数 */
const MAX_RECENT = 20;

// ============================================================
// 工具函数
// ============================================================

/**
 * 从 localStorage 读取最近浏览的市场 ID 列表
 * @returns 市场 ID 数组，最新的在最前面
 */
function readStoredIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * 将市场 ID 列表写入 localStorage
 * @param ids - 市场 ID 数组
 */
function writeStoredIds(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // localStorage 不可用时静默失败
  }
}

// ============================================================
// Hook 主体
// ============================================================

/**
 * 最近浏览市场 Hook
 * @description 提供记录浏览历史和获取最近浏览市场的能力
 * @param allMarkets - 所有市场数据，用于根据 ID 查找完整市场对象
 * @returns Hook 返回值
 */
export function useRecentMarkets(allMarkets: Market[]) {
  const [recentIds, setRecentIds] = useState<string[]>(() => readStoredIds());

  // 同步 localStorage 变化（跨 Tab 支持）
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setRecentIds(readStoredIds());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  /**
   * 记录一次市场浏览
   * @param marketId - 被浏览的市场 ID
   */
  const recordVisit = useCallback((marketId: string) => {
    setRecentIds((prev) => {
      // 去重：移除已存在的相同 ID，再插入到最前面
      const filtered = prev.filter((id) => id !== marketId);
      const updated = [marketId, ...filtered].slice(0, MAX_RECENT);
      writeStoredIds(updated);
      return updated;
    });
  }, []);

  /**
   * 清空浏览历史
   */
  const clearHistory = useCallback(() => {
    setRecentIds([]);
    writeStoredIds([]);
  }, []);

  /**
   * 根据 ID 列表查找完整市场对象，全部返回（由调用方决定展示数量）
   */
  const recentMarkets: Market[] = recentIds
    .map((id) => allMarkets.find((m) => m.id === id))
    .filter((m): m is Market => m !== undefined);

  return { recentMarkets, recentIds, recordVisit, clearHistory };
}
